import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  catalogue,
  type Locale,
  type LocalizedText,
  type MessageKey,
} from "@sirdab/returns-shared";

import {
  DEFAULT_LOCALE,
  directionFor,
  otherLocale,
  pick,
  readSessionLocale,
  resolveLocale,
  writeSessionLocale,
  type Direction,
} from "./locale";

export interface LocaleContextValue {
  locale: Locale;
  dir: Direction;
  setLocale: (locale: Locale) => void;
  toggleLocale: () => void;
  /** Resolve a catalogue key to a string in the active locale. */
  t: (key: MessageKey) => string;
  /** Render an API-supplied bilingual pair. The portal renders; it never translates. */
  text: (value: LocalizedText) => string;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export interface LocaleProviderProps {
  children: ReactNode;
  /** `policy.localeDefault`, once a lookup has returned one. */
  merchantDefault?: Locale | null | undefined;
  /** Test seam. Skips URL/session sniffing entirely. */
  initialLocale?: Locale | undefined;
}

function initialFromEnvironment(
  merchantDefault: Locale | null | undefined,
): Locale {
  if (typeof window === "undefined") return DEFAULT_LOCALE;
  const query = new URLSearchParams(window.location.search).get("lang");
  return resolveLocale({
    query,
    session: readSessionLocale(),
    merchant: merchantDefault ?? null,
    navigator: window.navigator?.language ?? null,
  });
}

/**
 * The single source of locale and direction for the whole app. Direction is applied once,
 * to `<html>` — no component sets its own `dir`, and no component uses a physical margin.
 */
export function LocaleProvider({
  children,
  merchantDefault,
  initialLocale,
}: LocaleProviderProps): React.JSX.Element {
  const [locale, setLocaleState] = useState<Locale>(
    () => initialLocale ?? initialFromEnvironment(merchantDefault),
  );

  const dir = directionFor(locale);

  useEffect(() => {
    if (typeof document === "undefined") return;
    document.documentElement.lang = locale;
    document.documentElement.dir = dir;
  }, [locale, dir]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    writeSessionLocale(next);
  }, []);

  const value = useMemo<LocaleContextValue>(
    () => ({
      locale,
      dir,
      setLocale,
      toggleLocale: () => setLocale(otherLocale(locale)),
      t: (key: MessageKey) => pick(catalogue[key], locale),
      text: (value_: LocalizedText) => pick(value_, locale),
    }),
    [locale, dir, setLocale],
  );

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
}

export function useMessages(): LocaleContextValue {
  const value = useContext(LocaleContext);
  if (value === null) {
    throw new Error("useMessages must be used inside <LocaleProvider>");
  }
  return value;
}

/** Alias for call sites that only care about the locale/direction, not the catalogue. */
export const useLocale = useMessages;
